import { ALERT_TYPES, PRODUCT_EXISTS_IN_CART_KEY } from '@nandosaus/constants';
import { concat, filter, find, get, head, isEmpty, isNil } from 'lodash';
import { PropTypes } from 'prop-types';
import { useRootStore } from '@nandosaus/state-management';
import { ensureProductDetailed } from '@nandosaus/state-management/lib/helpers/product-configuration';
import { useRouter } from 'next/router';
import React, { useEffect, useReducer, useRef, useState } from 'react';

import { useMediaQuery } from 'react-responsive';
import { Alert } from '../../alert';
import { Barcode } from '../../barcode';
import { Box, Flex } from '../../grid';
import { Button } from '../../button/new';
import { Modal } from '../../modal/new';
import { navigation } from '../../../constants';
import { offerPropTypes } from '../../../prop-types';
import { P } from '../../typography/new';
import { ProductDetailModal } from '../../product-detail-modal';
import { OfferChoiceList } from '../offer-choice-list';
import { OfferPromotionCard } from '../promotion-card';
import { useFeature } from '../../../hooks/use-feature';
import { logger } from '../../../utils/logger';

const REDUCER_EVENTS = {
  USE_ITEMS_IN_CART: 'useItemsInCart',
  SELECT_OFFER_PRODUCT: 'selectProduct',
  SELECT_PRODUCT_FOR_OFFER_EXTRA: 'selectProductForExtra',
  SELECT_OFFER_EXTRA: 'selectExtra',
};

const reducer = (state, action) => {
  switch (action.type) {
    case REDUCER_EVENTS.USE_ITEMS_IN_CART:
      return { selectedOfferProductPLU: null, isUsingExistingItemsInCart: true };
    case REDUCER_EVENTS.SELECT_OFFER_PRODUCT:
      return { selectedOfferProductPLU: action.plu, isUsingExistingItemsInCart: false };
    case REDUCER_EVENTS.SELECT_PRODUCT_FOR_OFFER_EXTRA:
      return { selectedProductForOfferExtraID: action.id, isUsingExistingItemsInCart: false };
    case REDUCER_EVENTS.SELECT_OFFER_EXTRA:
      return {
        selectedProductForOfferExtraID: state.selectedProductForOfferExtraID,
        selectedOfferExtraPLU: action.plu,
        isUsingExistingItemsInCart: false,
      };
    default:
      throw new Error(`${action.type} is not valid`);
  }
};

const initialState = {
  selectedOfferProductPLU: null,
  selectedProductForOfferExtraID: null,
  selectedOfferExtraPLU: null,
  isUsingExistingItemsInCart: false,
};

const OfferModal = ({ modalOpen, isApplied, handleModalClose: closeOfferModal, offer, onOfferChange, handleBack }) => {
  const { CartStore, ProductDetailsState, MenuStore } = useRootStore();
  const router = useRouter();

  // Every configurable-item path below is gated on this. With the flag off the
  // component must behave exactly as it did before: the isMealProductOffer bail
  // suppresses the whole picker and applies the offer without adding anything.
  const isConfigurableOfferItemsEnabled = useFeature('configurable-offer-items-enabled');

  const [productDetailModalOpen, setProductDetailModalOpen] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    selectedOfferProductPLU,
    isUsingExistingItemsInCart,
    selectedProductForOfferExtraID,
    selectedOfferExtraPLU,
  } = state;
  const {
    isAvailableInRestaurant,
    isAvailableOnline,
    availableProductOfferChoicesLabels,
    productOfferChoices,
    availableExtraOfferProductChoicesLabels,
    isForExtras,
    extrasInMenuApplicableForOffer,
    getAvailableExtraOfferExtraChoicesLabels,

    // Legacy whole-offer gate, kept only for the flag-off path: opening the PDP from the
    // offer modal used to throw for meal products, so an offer containing ANY meal PLU
    // had its entire choices picker suppressed and applied without adding anything.
    isMealProductOffer,
  } = offer;

  /**
   * Whether a given offer choice PLU needs configuring before it can validly enter the cart.
   *
   * Deliberately treats `null` (product details not loaded, so `choices` is empty and
   * cannot be trusted) the same as `true` — routing to the PDP is the safe answer. Treating
   * null as false is the bug this ticket exists to fix: it quick-adds a configurable
   * product with none of its required choices answered.
   */
  const choiceRequiresConfiguration = plu => {
    if (!plu || plu === PRODUCT_EXISTS_IN_CART_KEY) {
      return false;
    }

    // The label already carries the resolved value; fall back to the offer model for
    // PLUs that never made it into the label list.
    const label = find(availableProductOfferChoicesLabels, { value: plu });
    const requiresConfiguration = label ? label.requiresConfiguration : offer.offerChoiceRequiresConfiguration?.(plu);

    return requiresConfiguration !== false;
  };

  const displayBarcode = isAvailableInRestaurant;
  const offerHasPLUsButNoProductsFound =
    isEmpty(productOfferChoices) && isEmpty(extrasInMenuApplicableForOffer) && !isEmpty(offer.plus);

  const productsForExtraOffer = availableExtraOfferProductChoicesLabels;
  const extrasForExtraOffer = getAvailableExtraOfferExtraChoicesLabels(selectedProductForOfferExtraID);
  const showBrowseMenuButton = isForExtras && isEmpty(productsForExtraOffer);

  const handleOfferChange = () => {
    onOfferChange();

    if (!isApplied && router.pathname !== navigation.MENU && router.pathname !== navigation.CATERING) {
      router.push({ pathname: MenuStore.isCateringMenu ? navigation.CATERING : navigation.MENU, query: 'showCart' });
    }

    closeOfferModal();
  };

  const isOpeningProductConfiguration = useRef(false);

  /**
   * Open the PDP for an offer choice so its required choices can be answered.
   *
   * ensureProductDetailed has to run first: a meal PLU's item sibling is derived from
   * `otherSizes`, which are safeReferences that read as undefined until loaded, and the
   * PDP reads `displayProduct.name` off that sibling. This await is what makes a meal
   * coupon (e.g. QTRFREE1) render instead of erroring — the defect the old
   * isMealProductOffer bail was working around.
   *
   * The PDP renders as a CHILD of this modal, so the offer modal stays mounted behind it
   * and closing the PDP without adding returns the customer here with nothing applied.
   */
  const openProductConfiguration = async product => {
    if (isOpeningProductConfiguration.current) {
      return;
    }

    isOpeningProductConfiguration.current = true;

    try {
      await ensureProductDetailed({ menu: MenuStore.menu, product, logger });
      ProductDetailsState.viewProduct({ product });
      setProductDetailModalOpen(true);
    } finally {
      isOpeningProductConfiguration.current = false;
    }
  };

  useEffect(() => {
    const noOfferChoicesAvailable =
      (isForExtras && isEmpty(productsForExtraOffer)) || (!isForExtras && isEmpty(productOfferChoices));
    const offerChoiceAlreadyMade = !isNil(selectedOfferProductPLU) || !isNil(selectedProductForOfferExtraID);
    if (noOfferChoicesAvailable || offerChoiceAlreadyMade || isUsingExistingItemsInCart) {
      return;
    }

    if (offer.cartIsValidForOffer) {
      dispatch({ type: REDUCER_EVENTS.USE_ITEMS_IN_CART });
    } else if (!isEmpty(offer.plus)) {
      if (isForExtras) {
        const firstOrderItemInList = productsForExtraOffer[0].value;
        dispatch({ type: REDUCER_EVENTS.SELECT_PRODUCT_FOR_OFFER_EXTRA, id: firstOrderItemInList });
      } else {
        const availableProducts = filter(productOfferChoices, 'isAvailable');
        const firstProductInList = head(availableProducts)?.id;
        if (firstProductInList) {
          dispatch({ type: REDUCER_EVENTS.SELECT_OFFER_PRODUCT, plu: firstProductInList });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.cartIsValidForOffer, offer.plus, productOfferChoices, productsForExtraOffer]);

  /**
   * AC2 — keep the selection reflecting cart state across a cart round-trip.
   *
   * The effect above bails once any choice has been made, so a selection made before the
   * customer went off to the cart never caught up with what the cart now holds. This
   * re-syncs on the cart transition in both directions: once an offer product is in the
   * cart the selection becomes "apply to the item in your order", and if that item is
   * later removed the stale selection is cleared rather than silently re-adding it.
   */
  const wasCartValidForOffer = useRef(offer.cartIsValidForOffer);
  useEffect(() => {
    if (!isConfigurableOfferItemsEnabled || isApplied) {
      return;
    }

    const isCartValidForOffer = offer.cartIsValidForOffer;

    if (isCartValidForOffer === wasCartValidForOffer.current) {
      return;
    }

    wasCartValidForOffer.current = isCartValidForOffer;

    if (isCartValidForOffer) {
      dispatch({ type: REDUCER_EVENTS.USE_ITEMS_IN_CART });
    } else if (isUsingExistingItemsInCart) {
      dispatch({ type: REDUCER_EVENTS.SELECT_OFFER_PRODUCT, plu: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.cartIsValidForOffer, isConfigurableOfferItemsEnabled, isApplied, isUsingExistingItemsInCart]);

  const selectedChoiceNeedsConfiguration =
    isConfigurableOfferItemsEnabled && !isForExtras && choiceRequiresConfiguration(selectedOfferProductPLU);

  const selectedOfferProduct = find(productOfferChoices, { id: selectedOfferProductPLU });

  /**
   * Whether the PDP may show the "Make it a meal" block, which is what renders
   * <MealChoices /> — the meal's Side and Drink pickers.
   *
   * This is NOT a blanket false on the offer path, and the distinction matters:
   *
   *  - Offer PLU is an ITEM: keep it hidden. Upgrading migrates the submitted line
   *    to the meal sibling's PLU, and a coupon whose PLUList holds the item PLU
   *    then stops matching it (calculate-discount-prices filters on
   *    orderItem.product.plu), so the discount silently zeroes and RedCat rejects.
   *
   *  - Offer PLU is a MEAL: it must be shown. Side and Drink are REQUIRED choices
   *    of the coupon's own PLU (minimumOptionLimit 1), and the PDP renders the item
   *    sibling (displayProduct), so they live only inside this block. Hidden, they
   *    can never be answered, orderItem.isValid stays false forever, and "Add meal
   *    to order" silently does nothing. There is no migration risk here because the
   *    line already IS the meal PLU.
   */
  const showMealCtaForSelectedChoice = Boolean(selectedOfferProduct?.isMealProduct);

  const buttonCopy = (() => {
    if (isApplied) {
      return 'Remove offer from order';
    }

    if (isUsingExistingItemsInCart || (isNil(selectedOfferProductPLU) && isNil(selectedOfferExtraPLU))) {
      return 'Apply to order';
    }

    // A configurable or meal choice cannot be applied blind — the customer has to answer
    // its required choices first, so the CTA takes them to the PDP instead.
    if (selectedChoiceNeedsConfiguration) {
      return 'Edit item';
    }

    if (!isConfigurableOfferItemsEnabled && isMealProductOffer) {
      return 'Apply to order';
    }

    return 'Apply and add to order';
  })();

  const button = isAvailableOnline
    ? {
        primary: (
          <Button
            size="tall"
            widthType="fit"
            disabled={!isApplied && isForExtras && !selectedOfferExtraPLU && !isUsingExistingItemsInCart}
            onClick={() => {
              if (isApplied) {
                onOfferChange();
                closeOfferModal();
                return;
              }

              const selectedProduct = find(productOfferChoices, { id: selectedOfferProductPLU });

              if (isConfigurableOfferItemsEnabled) {
                if (selectedChoiceNeedsConfiguration && selectedProduct) {
                  openProductConfiguration(selectedProduct);
                  return;
                }
              } else {
                const choicesRequired = !isEmpty(get(selectedProduct, 'choices'));

                if (isMealProductOffer) {
                  handleOfferChange();
                  return;
                }

                if (choicesRequired) {
                  ProductDetailsState.viewProduct({ product: selectedProduct });
                  setProductDetailModalOpen(true);
                  return;
                }
              }

              if (!isUsingExistingItemsInCart && selectedProduct) {
                CartStore.addOrderItem({ product: selectedProduct });
              }

              if (!isUsingExistingItemsInCart && selectedProductForOfferExtraID) {
                const cartOrderItem = find(
                  CartStore.orderItems,
                  orderItem => orderItem.id === selectedProductForOfferExtraID
                );

                const { product } = cartOrderItem;
                const productExtraChoice = product.extraChoice;
                const productExtraChoiceName = get(productExtraChoice, 'name');
                const existingExtrasForCartItem = cartOrderItem.choices.get(productExtraChoiceName) || [];
                CartStore.updateChoice(
                  cartOrderItem,
                  productExtraChoiceName,
                  concat(existingExtrasForCartItem, find(productExtraChoice.options, { plu: selectedOfferExtraPLU }))
                );
              }

              handleOfferChange();
            }}
          >
            {buttonCopy}
          </Button>
        ),
      }
    : undefined;

  const barcodeWidth = useMediaQuery({ query: '(max-width: 390px)' }) ? 1 : 2;

  return (
    <Modal
      open={modalOpen}
      showShadow
      modalTitle="Promotion details"
      handleClose={closeOfferModal}
      hideBottomDecal={false}
      buttons={button}
      handleBack={handleBack}
      // AC1 (modal taller than the viewport stays fully reachable) is already satisfied by
      // modal/new's default: hasInnerScroll={false} puts `overflow-y: auto` on the content
      // box, which the 85% maxHeight then clamps. Do NOT set hasInnerScroll here — despite
      // the name it means "a child owns the scrolling" (see cart-preview-modal) and would
      // remove this modal's own scroll, making tall offers unreachable.
    >
      <Flex flexDirection="column" gap={{ xs: '24px', lg: '32px' }}>
        <Box mt="16px" alignSelf="center">
          <OfferPromotionCard
            offer={offer}
            isApplied={isApplied}
            hideButton
            borderRadius={{ xs: '24px', lg: '32px' }}
          />
        </Box>
        {displayBarcode && (
          <Flex flexDirection="column" background="white" borderRadius="24px" alignItems="center" p="26px 23px">
            <Flex flexDirection="column" alignItems="center" gap="17px">
              <P variant="small-semi-bold" textAlign="center">
                Scan to redeem in restaurant
              </P>
              <Barcode value={offer.barcode} width={barcodeWidth} />
            </Flex>
          </Flex>
        )}

        {!isApplied && (
          <>
            {offerHasPLUsButNoProductsFound && isAvailableOnline && (
              <Flex flexDirection="column">
                <P variant="medium-semi-bold" color="black" pb={{ xs: '20px', lg: '24px' }}>
                  Offer choices
                </P>
                <Alert type={ALERT_TYPES.WARNING}>No choices available at your current restaurant</Alert>
              </Flex>
            )}

            {/* The isMealProductOffer gate is deliberately only applied with the flag off. It is
                true when ANY plu on the offer is a meal, so a mixed meal/non-meal offer had its
                entire picker suppressed — including the choices that were perfectly addable. */}
            {!isEmpty(availableProductOfferChoicesLabels) &&
              !isForExtras &&
              (isConfigurableOfferItemsEnabled || !isMealProductOffer) && (
                <OfferChoiceList
                  title="Offer choices"
                  required
                  items={availableProductOfferChoicesLabels}
                  selectedItems={[isUsingExistingItemsInCart ? PRODUCT_EXISTS_IN_CART_KEY : selectedOfferProductPLU]}
                  onSelect={selectedValues => {
                    const plu = selectedValues[0];

                    if (plu.value === PRODUCT_EXISTS_IN_CART_KEY) {
                      dispatch({ type: REDUCER_EVENTS.USE_ITEMS_IN_CART });
                      return;
                    }

                    dispatch({ type: REDUCER_EVENTS.SELECT_OFFER_PRODUCT, plu: selectedValues[0].value });
                  }}
                />
              )}
            {!isEmpty(productsForExtraOffer) && (
              <>
                <OfferChoiceList
                  title="Apply extra to"
                  required
                  items={productsForExtraOffer}
                  selectedItems={[
                    isUsingExistingItemsInCart ? PRODUCT_EXISTS_IN_CART_KEY : selectedProductForOfferExtraID,
                  ]}
                  onSelect={selectedValues => {
                    const selectedValue = selectedValues[0];

                    if (selectedValue.value === PRODUCT_EXISTS_IN_CART_KEY) {
                      dispatch({ type: REDUCER_EVENTS.USE_ITEMS_IN_CART });
                      return;
                    }

                    dispatch({ type: REDUCER_EVENTS.SELECT_PRODUCT_FOR_OFFER_EXTRA, id: selectedValue.value });
                  }}
                />
                {!isEmpty(extrasForExtraOffer) && (
                  <OfferChoiceList
                    title="Choose an extra"
                    required
                    items={extrasForExtraOffer}
                    selectedItems={[selectedOfferExtraPLU]}
                    onSelect={selectedValues => {
                      dispatch({ type: REDUCER_EVENTS.SELECT_OFFER_EXTRA, plu: selectedValues[0].value });
                    }}
                  />
                )}
              </>
            )}
            {showBrowseMenuButton && (
              <Flex flexDirection="column">
                <P mb={{ xs: '24px', lg: '32px' }}>
                  Extras only apply to a select range of products within the Nando’s menu. Please browse our menu and
                  add a burger, wrap, pita, salad or paella to apply this promo.
                </P>
                <Button
                  size="tall"
                  variant="secondary"
                  widthType="fullWidth"
                  isFullWidth
                  href={navigation.MENU}
                  onClick={() => closeOfferModal()}
                >
                  Browse Menu
                </Button>
              </Flex>
            )}
          </>
        )}

        {offer.longDescription && (
          <Flex flexDirection="column">
            <P>{offer.longDescription}</P>
          </Flex>
        )}

        {productDetailModalOpen && (
          <ProductDetailModal
            showMealCta={showMealCtaForSelectedChoice}
            modalOpen={productDetailModalOpen}
            // Closing without adding drops straight back to this modal with nothing applied.
            handleModalClose={() => setProductDetailModalOpen(false)}
            handleModalSubmit={() => {
              setProductDetailModalOpen(false);
              handleOfferChange();
            }}
          />
        )}
      </Flex>
    </Modal>
  );
};

OfferModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  isApplied: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  offer: offerPropTypes.isRequired,
  onOfferChange: PropTypes.func.isRequired,
  handleBack: PropTypes.func,
};

OfferModal.defaultProps = {
  handleBack: null,
};

export { OfferModal };
